// Zähler in der Hausmeister-App (#616, Björn 04.11.2026): Hausmeister mit dem Modul „Zähler“
// (Team → Berechtigungen) sehen alle Zähler, legen neue an und erfassen Stände. Bearbeiten und
// Löschen bleibt im Büro. Alles läuft über eigene Datenbankfunktionen (Migration
// 2026-11-04-hausmeister-zaehler.sql) — die Büro-Tabellen bleiben für Hausmeister zu.
import { supabase } from '../supabase/client';
import { leseZahl } from '../immobilien/mietverhaeltnisFormular';
import { fotoEndung } from '../zaehler/ablesungen';
import { FOTO_BUCKET } from '../zaehler/zaehlerApi';

export interface HmHaus { id: string; bezeichnung: string | null; strasse: string | null; hausnummer: string | null }

export interface HmObjekt {
  id: string; objektnummer: string | null; bezeichnung: string | null;
  strasse: string | null; hausnummer: string | null; plz: string | null; stadt: string | null;
  haeuser: HmHaus[];
}

export interface HmZaehler {
  id: string; zaehler_nummer: string; typ: string; einheit: string | null; standort: string | null;
  einheit_bezeichnung: string | null; aktiv: boolean; objekt_id: string; haus_id: string | null; haus: string | null;
  letzte_datum: string | null; letzter_wert: number | null; ablesungen: number;
}

/** Hat der angemeldete Hausmeister die Zähler-Freigabe? Fehler zählen als „nein“ (Reiter bleibt weg). */
export async function darfZaehler(): Promise<boolean> {
  const { data, error } = await supabase.rpc('hausmeister_darf_zaehler');
  return !error && data === true;
}

export async function ladeObjekte(): Promise<HmObjekt[]> {
  const { data, error } = await supabase.rpc('hausmeister_zaehler_objekte');
  if (error) throw new Error(error.message);
  return ((data ?? []) as HmObjekt[]).map(o => ({ ...o, haeuser: Array.isArray(o.haeuser) ? o.haeuser : [] }));
}

export async function ladeZaehler(): Promise<HmZaehler[]> {
  const { data, error } = await supabase.rpc('hausmeister_zaehler_liste');
  if (error) throw new Error(error.message);
  return ((data ?? []) as HmZaehler[]).map(z => ({
    ...z, letzter_wert: z.letzter_wert === null ? null : Number(z.letzter_wert), ablesungen: Number(z.ablesungen ?? 0),
  }));
}

export interface NeuerZaehler {
  objektId: string; nummer: string; typ: string; einheit: string; standort: string; hausId: string; wohnung: string; notizen: string;
}

export async function legeZaehlerAn(z: NeuerZaehler): Promise<string> {
  const { data, error } = await supabase.rpc('hausmeister_zaehler_anlegen', {
    p_objekt_id: z.objektId, p_zaehler_nummer: z.nummer, p_typ: z.typ, p_einheit: z.einheit || null, p_standort: z.standort || null,
    p_haus_id: z.hausId || null, p_einheit_bezeichnung: z.wohnung || null, p_notizen: z.notizen || null,
  });
  if (error) throw new Error(error.message);
  return data as string;
}

/** Beleg-Foto in den Ordner des Zählers laden; liefert den Pfad im Bucket (die URL baut die Datenbank). */
export async function ladeFotoHoch(zaehlerId: string, datei: File): Promise<string> {
  if (!datei.type.startsWith('image/')) throw new Error('Bitte ein Bild auswählen.');
  const pfad = `${zaehlerId}/${Date.now()}.${fotoEndung(datei.name)}`;
  const up = await supabase.storage.from(FOTO_BUCKET).upload(pfad, datei, { contentType: datei.type });
  if (up.error) throw new Error(`Foto konnte nicht hochgeladen werden (${up.error.message}).`);
  return pfad;
}

export async function erfasseStand(zaehlerId: string, datum: string, wert: number, fotoPfad: string | null, notizen: string): Promise<void> {
  const { error } = await supabase.rpc('hausmeister_ablesung_erfassen', {
    p_zaehler_id: zaehlerId, p_datum: datum, p_wert: wert, p_foto_pfad: fotoPfad, p_notizen: notizen || null,
  });
  if (error) throw new Error(error.message);
}

// ── reine Funktionen ──────────────────────────────────────────────────────────

/** „0001 · Alte Straße 17“ */
export function objektText(o: Pick<HmObjekt, 'objektnummer' | 'bezeichnung' | 'strasse' | 'hausnummer'> | undefined): string {
  if (!o) return 'Objekt';
  const adresse = [o.strasse, o.hausnummer].filter(Boolean).join(' ');
  const name = o.bezeichnung?.trim() || adresse;
  return `${o.objektnummer ? `${o.objektnummer} · ` : ''}${name || 'Objekt'}`;
}

/** Suche über Nummer, Standort, Wohnung, Haus und Objekt; ohne Text alle. */
export function sucheZaehler(liste: readonly HmZaehler[], objekte: ReadonlyMap<string, HmObjekt>, text: string, objektId: string): HmZaehler[] {
  const t = text.trim().toLowerCase();
  return liste.filter(z => {
    if (objektId && z.objekt_id !== objektId) return false;
    if (!t) return true;
    const o = objekte.get(z.objekt_id);
    return [z.zaehler_nummer, z.standort, z.einheit_bezeichnung, z.haus, o?.objektnummer, o?.bezeichnung, o?.strasse, o?.stadt]
      .some(f => (f ?? '').toLowerCase().includes(t));
  });
}

export interface StandPruefung { wert: number | null; fehler: string | null; warnung: string | null }

/** Eingabe prüfen: Zahl nötig; Warnung, wenn der Stand unter dem letzten liegt (Zähler laufen vorwärts). */
export function pruefeStand(eingabe: string, datum: string, heute: string, z: Pick<HmZaehler, 'letzter_wert' | 'letzte_datum'>): StandPruefung {
  if (!datum) return { wert: null, fehler: 'Bitte ein Datum angeben.', warnung: null };
  if (datum > heute) return { wert: null, fehler: 'Das Datum liegt in der Zukunft.', warnung: null };
  const wert = leseZahl(eingabe);
  if (wert === null) return { wert: null, fehler: 'Bitte den Zählerstand eingeben.', warnung: null };
  const warnung = z.letzter_wert !== null && z.letzte_datum !== null && datum >= z.letzte_datum && wert < z.letzter_wert
    ? 'Der Stand ist kleiner als der letzte. Bitte prüfen – oder ist es ein neuer Zähler?'
    : null;
  return { wert, fehler: null, warnung };
}
